/**
 * SavedViewsBar — dropdown shell above an AdvancedTable.
 *
 * Feb 2026 R291d · Rewritten from a chip row to a dropdown so long view
 * lists stop wrapping onto 2-3 rows (and so mobile doesn't lose vertical
 * room to view chips). Behaviour kept 1:1 with the chip version:
 *
 *   • Trigger button shows the active view name + icon
 *   • Dropdown lists workspace views (star) then personal views (user)
 *     with per-item defaults markers and user-count badges
 *   • Each row has a trailing "⋯" that opens the same action popover
 *     used before (set default, rename, overwrite, delete)
 *   • Save-view / Overwrite-active / Admin "Manage all views" / Reset
 *     stay as inline buttons on the right so power-users still see them
 *   • All existing `data-testid`s preserved so e2e doesn't break
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Star, User, MoreVertical, Bookmark, RotateCcw, Save, ShieldCheck,
  ChevronDown, Layers,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import ManageAllViewsDialog from "@/components/table/ManageAllViewsDialog";

export default function SavedViewsBar({ state, tableKey: _tableKey, inline = false }) {
  const {
    views, activeViewId, wsDefaultId, userDefaultId, isAdmin, dirty,
    applyView, saveNewView, updateView, deleteView,
    setWorkspaceDefault, setUserDefault, clearUserDefault, resetToDefaults,
    currentLayout,
  } = state;
  const { user } = useAuth();
  const currentUserId = user?.id;

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: "", description: "", scope: "user" });
  const [mgrOpen, setMgrOpen] = useState(false);

  const wsViews = views.filter((v) => v.scope === "workspace");
  const userViews = views.filter((v) => v.scope === "user");
  const activeView = views.find((v) => v.id === activeViewId);

  const onSave = async () => {
    const v = await saveNewView(saveForm);
    if (v) { setSaveOpen(false); setSaveForm({ name: "", description: "", scope: "user" }); }
  };

  // Trigger button label — shows the active view name or a neutral
  // "No view" placeholder plus a short count hint.
  const triggerLabel = activeView ? activeView.name : "No view";
  const triggerIcon = activeView
    ? (activeView.scope === "workspace" ? <Star className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />)
    : <Layers className="w-3.5 h-3.5" />;

  // Feb 2026 R291k · `inline` mode drops the wrapping bar so the
  // AdvancedTable toolbar can host the views picker on the SAME row
  // as Search/Options/count. The content is otherwise identical.
  //
  // Oct 2026 round 7 · Previously `Wrap` was defined INLINE inside the
  // render body as a new arrow component on every render. React treats
  // a fresh component identity as a brand-new tree → the entire sub-
  // tree (including the Save-view Dialog's Input) unmounts and remounts
  // on every keystroke, which was eating focus and resetting the field.
  // Fix: unconditionally render a Fragment-OR-div wrapper directly at
  // return time so the children's identity is stable.

  const content = (
    <>
      {!inline && <span className="text-[10px] uppercase tracking-widest text-muted-foreground pr-1">Views</span>}

      {/* View picker — single dropdown. Clicking an item applies the view;
          the trailing "⋯" opens a per-view action popover. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 border h-8 px-2 text-xs min-w-[140px] max-w-[220px] rounded-none ${activeView ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}
            data-testid={`views-picker-trigger-${_tableKey}`}
            title={activeView ? `Current view: ${activeView.name}` : "Pick a saved view"}
          >
            {triggerIcon}
            <span className="truncate flex-1 text-left">{triggerLabel}</span>
            {userDefaultId === activeViewId && activeView && <Bookmark className="w-3 h-3 fill-current shrink-0" />}
            <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 max-h-[70vh] overflow-y-auto"
                             data-testid={`views-picker-menu-${_tableKey}`}>
          {wsViews.length > 0 && (
            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Workspace views
            </DropdownMenuLabel>
          )}
          {wsViews.map((v) => (
            <ViewRow
              key={v.id}
              view={v}
              active={v.id === activeViewId}
              isWsDefault={v.id === wsDefaultId}
              isUserDefault={v.id === userDefaultId}
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              onClick={() => applyView(v)}
              onSetWsDefault={() => setWorkspaceDefault(v.id)}
              onSetUserDefault={() => setUserDefault(v.id)}
              onClearUserDefault={clearUserDefault}
              onUpdate={(patch) => updateView(v.id, patch)}
              onOverwrite={() => updateView(v.id, { name: v.name, description: v.description, config: currentLayout })}
              onDelete={() => deleteView(v.id)}
              testKey={v.id}
            />
          ))}

          {wsViews.length > 0 && userViews.length > 0 && <DropdownMenuSeparator />}

          {userViews.length > 0 && (
            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
              My views
            </DropdownMenuLabel>
          )}
          {userViews.map((v) => (
            <ViewRow
              key={v.id}
              view={v}
              active={v.id === activeViewId}
              isWsDefault={false}
              isUserDefault={v.id === userDefaultId}
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              onClick={() => applyView(v)}
              onSetWsDefault={() => setWorkspaceDefault(v.id)}
              onSetUserDefault={() => setUserDefault(v.id)}
              onClearUserDefault={clearUserDefault}
              onUpdate={(patch) => updateView(v.id, patch)}
              onOverwrite={() => updateView(v.id, { name: v.name, description: v.description, config: currentLayout })}
              onDelete={() => deleteView(v.id)}
              testKey={v.id}
            />
          ))}

          {views.length === 0 && (
            <div className="px-2 py-3 text-xs italic text-muted-foreground">
              No saved views yet. Use <strong>Save view</strong> to capture the current layout.
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Feb 2026 R293q · Icon-only inline buttons so the AdvancedTable
          toolbar fits on a single row (Search → density → view →
          Options → count). Tooltips carry the full label; dirty state
          still pulses the Save button. */}
      <button
        type="button"
        onClick={() => setSaveOpen(true)}
        className={`inline-flex items-center justify-center h-8 w-8 border border-dashed border-primary/40 text-primary hover:bg-primary/10 rounded-none ${dirty ? "animate-pulse" : ""}`}
        data-testid={`table-save-view-btn-${_tableKey}`}
        aria-label={dirty ? "Save current view" : "Save view"}
        title={dirty ? "Save current view" : "Save view"}
      >
        <Save className="w-3.5 h-3.5" />
      </button>

      {activeView && dirty && (
        <button
          type="button"
          onClick={() => updateView(activeView.id, { name: activeView.name, description: activeView.description, config: currentLayout })}
          className="inline-flex items-center justify-center h-8 w-8 border border-border hover:bg-muted rounded-none"
          data-testid={`table-update-active-view-${_tableKey}`}
          aria-label={`Overwrite "${activeView.name}"`}
          title={`Overwrite "${activeView.name}"`}
        >
          <Bookmark className="w-3.5 h-3.5" />
        </button>
      )}

      {isAdmin && (
        <button
          type="button"
          onClick={() => setMgrOpen(true)}
          className="inline-flex items-center justify-center h-8 w-8 border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 rounded-none"
          data-testid={`table-manage-all-views-${_tableKey}`}
          aria-label="Manage all views"
          title="Admin — manage all users' views + role defaults">
          <ShieldCheck className="w-3.5 h-3.5" />
        </button>
      )}

      <button
        type="button"
        onClick={resetToDefaults}
        className="inline-flex items-center justify-center h-8 w-8 border border-border text-muted-foreground hover:text-foreground hover:bg-muted rounded-none"
        data-testid={`table-reset-defaults-${_tableKey}`}
        aria-label="Reset to defaults"
        title="Reset to defaults · clear all customisations"
      >
        <RotateCcw className="w-3.5 h-3.5" />
      </button>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle>Save current view</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Capture the current filters, sort, columns and search as a reusable view. You can share it with the whole workspace or keep it private.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Name *</Label>
              <Input
                value={saveForm.name}
                onChange={(e) => setSaveForm((s) => ({ ...s, name: e.target.value }))}
                placeholder='e.g. "Overdue > 30d"'
                className="h-10 rounded-none"
                data-testid="save-view-name-input"
                maxLength={80}
              />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Description</Label>
              <Input
                value={saveForm.description}
                onChange={(e) => setSaveForm((s) => ({ ...s, description: e.target.value }))}
                placeholder="Optional"
                className="h-10 rounded-none"
                data-testid="save-view-desc-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Who can see this?</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSaveForm((s) => ({ ...s, scope: "user" }))}
                  className={`flex-1 inline-flex items-center gap-2 border p-2 text-sm ${saveForm.scope === "user" ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}
                  data-testid="save-view-scope-user"
                >
                  <User className="w-4 h-4" /> Only me
                </button>
                <button
                  type="button"
                  onClick={() => setSaveForm((s) => ({ ...s, scope: "workspace" }))}
                  className={`flex-1 inline-flex items-center gap-2 border p-2 text-sm ${saveForm.scope === "workspace" ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}
                  data-testid="save-view-scope-workspace"
                >
                  <Star className="w-4 h-4" /> Everyone
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={onSave} className="rounded-none" data-testid="save-view-save-btn">Save view</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {mgrOpen && (
        <ManageAllViewsDialog open={mgrOpen}
                              onClose={() => setMgrOpen(false)}
                              state={state}
                              tableKey={_tableKey} />
      )}
    </>
  );

  if (inline) return content;
  return (
    <div className="flex items-center gap-1.5 flex-wrap py-2 px-3 border-b border-border bg-muted/20"
         data-testid={`saved-views-bar-${_tableKey}`}>
      {content}
    </div>
  );
}

/**
 * ViewRow — a single row inside the views dropdown.
 *
 * Main area is a clickable button that applies the view. The trailing
 * "⋯" opens the same per-view action popover used by the old chip. We
 * stop propagation so clicking the "⋯" doesn't fire the apply handler.
 */
function ViewRow({ view, active, isWsDefault, isUserDefault, isAdmin, currentUserId,
                   onClick, onSetWsDefault, onSetUserDefault, onClearUserDefault,
                   onUpdate, onOverwrite, onDelete, testKey }) {
  const [renOpen, setRenOpen] = useState(false);
  const [name, setName] = useState(view.name);

  const parts = [];
  if (view.created_by_name) parts.push(`by ${view.created_by_name}`);
  if (view.created_at) parts.push(`created ${view.created_at.slice(0, 10)}`);
  if (view.updated_at && view.updated_at !== view.created_at) parts.push(`edited ${view.updated_at.slice(0, 10)}`);
  if (isUserDefault) parts.push("your default");
  if (view.user_default_count) parts.push(`${view.user_default_count} user${view.user_default_count === 1 ? "" : "s"}' default`);
  const tooltip = `${view.name}\n${parts.join(" · ")}`;

  return (
    <DropdownMenuItem
      // Keep the dropdown open when the trailing "⋯" is clicked so the
      // sub-popover can render.
      onSelect={(e) => { e.preventDefault(); onClick(); }}
      className={`flex items-center gap-1 ${active ? "bg-muted/60 font-semibold" : ""}`}
      data-testid={`view-chip-${testKey}`}
      title={tooltip}
    >
      <span className="shrink-0">
        {view.scope === "workspace" ? <Star className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
      </span>
      <span className="flex-1 truncate" data-testid={`view-chip-apply-${testKey}`}>{view.name}</span>
      {isUserDefault && <Bookmark className="w-3 h-3 fill-current shrink-0" title="Your default" />}
      {isWsDefault && !active && <span className="text-[9px] uppercase tracking-wider opacity-70">default</span>}
      {view.user_default_count > 0 && !active && (
        <span className="text-[9px] font-mono opacity-70" data-testid={`view-chip-users-${testKey}`}>·{view.user_default_count}</span>
      )}

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            /* Feb 2026 R293l · Suppress every pointer / key event that
               Radix listens to on the parent DropdownMenuItem so
               clicking "⋯" opens the sub-popover INSTEAD of selecting
               (and closing) the parent menu. Previously the parent
               dropdown closed the moment you clicked here and the
               sub-popover never had a chance to render. */
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.stopPropagation(); }}
            className="border-l border-current/20 pl-1 ml-1 opacity-70 hover:opacity-100"
            data-testid={`view-chip-menu-${testKey}`}
            aria-label="View options"
          >
            <MoreVertical className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1 rounded-none"
                         onClick={(e) => e.stopPropagation()}
                         onPointerDown={(e) => e.stopPropagation()}
                         onPointerUp={(e) => e.stopPropagation()}
                         data-testid={`view-chip-menu-content-${testKey}`}>
          {isUserDefault ? (
            <MenuItem onClick={onClearUserDefault} testid={`view-menu-clear-mydefault-${testKey}`}>
              Remove as my default
            </MenuItem>
          ) : (
            <MenuItem onClick={onSetUserDefault} testid={`view-menu-set-mydefault-${testKey}`}>
              📌 Pin as my default
            </MenuItem>
          )}
          {isAdmin && view.scope === "workspace" && !isWsDefault && (
            <MenuItem onClick={onSetWsDefault} testid={`view-menu-set-wsdefault-${testKey}`}>
              ⭐ Set as workspace default
            </MenuItem>
          )}
          <MenuItem onClick={() => { setName(view.name); setRenOpen(true); }} testid={`view-menu-rename-${testKey}`}>
            Rename…
          </MenuItem>
          <MenuItem onClick={onOverwrite} testid={`view-menu-overwrite-${testKey}`}>
            Overwrite with current
          </MenuItem>
          <MenuItem
            onClick={onDelete}
            className="text-red-600"
            testid={`view-menu-delete-${testKey}`}
          >
            Delete view
          </MenuItem>
        </PopoverContent>
      </Popover>

      <Dialog open={renOpen} onOpenChange={setRenOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename view</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Give this view a new name. All other settings stay the same.
            </DialogDescription>
          </DialogHeader>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 rounded-none" data-testid={`view-rename-input-${testKey}`} maxLength={80} />
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setRenOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={() => { onUpdate({ name: name.trim() || view.name }); setRenOpen(false); }} data-testid={`view-rename-save-${testKey}`}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DropdownMenuItem>
  );
}

function MenuItem({ children, onClick, className = "", disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left text-sm px-2.5 py-1.5 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
